import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { useAuth, logout } from "@/hooks/use-auth";
import { useEngineers, useCreateEngineer, useUpdateEngineer } from "@/hooks/use-engineers";
import { useInspections, useDeleteInspection } from "@/hooks/use-inspections";
import { Wrench, Users, FileText, ChevronLeft, Plus, Edit2, Check, X, Eye, Power, PowerOff, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function AdminEngineerPPM() {
  const { data: user, isLoading: isAuthLoading } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    if (!isAuthLoading) {
      if (!user) {
        setLocation("/admin");
      } else if (user.role !== "admin") {
        setLocation("/engineer");
      }
    }
  }, [user, isAuthLoading, setLocation]);

  const [activeTab, setActiveTab] = useState<"inspections" | "engineers">("inspections");

  if (isAuthLoading || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/admin/dashboard" className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900 transition-colors">
              <ChevronLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2 text-primary">
              <Wrench className="w-5 h-5" />
              <h1 className="font-bold text-lg text-slate-900">Engineer PPM Portal</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-slate-600 hidden sm:inline-block">Admin: {user.name}</span>
            <button onClick={logout} className="text-sm font-medium text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors">Logout</button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-6 lg:p-8 space-y-6">
        <div className="flex bg-white rounded-xl border border-slate-200 p-1 w-full max-w-sm">
          <button
            onClick={() => setActiveTab("inspections")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === "inspections" ? "bg-slate-100 text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            <FileText className="w-4 h-4" /> All Inspections
          </button>
          <button
            onClick={() => setActiveTab("engineers")}
            className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === "engineers" ? "bg-slate-100 text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          >
            <Users className="w-4 h-4" /> Manage Engineers
          </button>
        </div>

        {activeTab === "inspections" ? <InspectionsList /> : <EngineersList />}
      </main>
    </div>
  );
}

function InspectionsList() {
  const { data: inspections, isLoading } = useInspections();
  const deleteInspection = useDeleteInspection();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  if (isLoading) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">PPM Inspections</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wider font-semibold text-slate-500 bg-slate-50">
              <th className="p-4">Date</th>
              <th className="p-4">Site & Asset</th>
              <th className="p-4">Engineer</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {!inspections?.length ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-slate-500">No inspections found.</td>
              </tr>
            ) : (
              inspections.map(insp => (
                <tr key={insp.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-4 whitespace-nowrap">
                    {insp.data.inspectionDate ? new Date(insp.data.inspectionDate).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-4 min-w-[200px]">
                    <div className="font-medium text-slate-900">{insp.data.siteName || "Unnamed Site"}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{insp.data.assetId || "No Asset ID"}</div>
                  </td>
                  <td className="p-4 whitespace-nowrap text-slate-600">{insp.engineerName}</td>
                  <td className="p-4 whitespace-nowrap">
                    {insp.status === "completed" ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
                        <Check className="w-3 h-3" /> Completed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
                        <Edit2 className="w-3 h-3" /> Draft
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setLocation(`/engineer/inspection/${insp.id}`)}
                        className="p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900 rounded-lg transition-colors"
                        title={insp.status === "completed" ? "View/Edit" : "Edit"}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm("Are you sure you want to delete this inspection? This action cannot be undone.")) {
                            deleteInspection.mutate(insp.id, {
                              onSuccess: () => toast({ title: "Inspection deleted" }),
                            });
                          }
                        }}
                        className="p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EngineersList() {
  const { data: engineers, isLoading } = useEngineers();
  const createEngineer = useCreateEngineer();
  const updateEngineer = useUpdateEngineer();
  const { toast } = useToast();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });

  if (isLoading) return <div className="py-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingId) {
      const payload: Partial<typeof formData> = { ...formData };
      if (!payload.password) delete payload.password; // Omit blank password on edit
      updateEngineer.mutate({ id: editingId, data: payload }, {
        onSuccess: () => {
          toast({ title: "Engineer updated" });
          setIsFormOpen(false);
          setEditingId(null);
        },
        onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
      });
    } else {
      createEngineer.mutate(formData, {
        onSuccess: () => {
          toast({ title: "Engineer created" });
          setIsFormOpen(false);
        },
        onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
      });
    }
  };

  const openNew = () => {
    setEditingId(null);
    setFormData({ name: "", email: "", password: "" });
    setIsFormOpen(true);
  };

  const openEdit = (eng: any) => {
    setEditingId(eng.id);
    setFormData({ name: eng.name, email: eng.email, password: "" });
    setIsFormOpen(true);
  };

  const toggleActive = (eng: any) => {
    updateEngineer.mutate({ id: eng.id, data: { active: !eng.active } });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white p-4 border border-slate-200 rounded-xl shadow-sm">
        <h2 className="font-semibold text-slate-900">Engineer Accounts</h2>
        <button onClick={openNew} className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          <Plus className="w-4 h-4" /> New Engineer
        </button>
      </div>

      {isFormOpen && (
        <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-900">{editingId ? "Edit Engineer" : "Create Engineer"}</h3>
            <button type="button" onClick={() => setIsFormOpen(false)} className="p-1 text-slate-400 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
              <input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Email Address</label>
              <input required type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1.5">Password {editingId && "(leave blank to keep current)"}</label>
              <input required={!editingId} minLength={12} type="text" placeholder="Min 12 characters (include upper, lower, number, symbol)" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 text-sm" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button disabled={createEngineer.isPending || updateEngineer.isPending} type="submit" className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50">
              Save Engineer
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-slate-200 text-xs uppercase tracking-wider font-semibold text-slate-500 bg-slate-50">
              <th className="p-4">Engineer Name</th>
              <th className="p-4">Email</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {!engineers?.length ? (
              <tr><td colSpan={4} className="p-8 text-center text-slate-500">No engineers created yet.</td></tr>
            ) : (
              engineers.map(eng => (
                <tr key={eng.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="p-4 font-medium text-slate-900">{eng.name}</td>
                  <td className="p-4 text-slate-600">{eng.email}</td>
                  <td className="p-4">
                    {eng.active ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">Active</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-medium border border-slate-200">Deactivated</span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(eng)} className="p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-900 rounded-lg transition-colors" title="Edit">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => toggleActive(eng)} className={`p-1.5 rounded-lg transition-colors ${eng.active ? "text-slate-400 hover:bg-red-50 hover:text-red-600" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"}`} title={eng.active ? "Deactivate" : "Activate"}>
                        {eng.active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
